/* global axios */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { CAPTAIN_ERROR_TYPES } from 'dashboard/composables/captain/constants';

/**
 * Alex Copilot: routes the native Copilot composer flow (sparkle button,
 * CopilotMenuBar, inline CopilotEditor with accept/discard and follow-up) to
 * the Alex Captain transport (`POST /api/v1/accounts/:id/alex_captain/tasks`)
 * when the account setting `alex_captain_enabled` is on.
 *
 * The transport knows three tasks (`suggest`, `summarize`, `rewrite`) and one
 * free-text instruction. Every native rewrite operation and every follow-up is
 * expressed as a `rewrite` instruction built here; the server treats it as
 * untrusted data and never sends anything to the customer.
 */
export const ALEX_COPILOT_TRANSPORT_VERSION = 'captain.transport.v1';
export const ALEX_COPILOT_FOLLOW_UP_VERSION = 'alex-copilot.follow-up.v1';
const MAX_TEXT_LENGTH = 16000;
// Above the Rails client (90 s) and below nginx (100 s): the server's own error, not an axios
// abort, is what the attendant sees. Alex itself stops first at CAPTAIN_TIMEOUT_MS (85 s).
const REQUEST_TIMEOUT_MS = 95000;

// Native Copilot action keys mapped to an instruction for the Alex `rewrite`
// task. The Alex Copilot prompt and the attendants write in pt-BR.
const REWRITE_INSTRUCTIONS = Object.freeze({
  improve:
    'Melhore a resposta do atendente abaixo, mantendo o sentido, os fatos e o idioma. Devolva somente o texto final.',
  fix_spelling_grammar:
    'Corrija apenas gramática, ortografia e pontuação do texto abaixo, sem mudar o sentido nem o tom. Devolva somente o texto corrigido.',
  professional:
    'Reescreva o texto abaixo em tom profissional, mantendo o sentido e os fatos. Devolva somente o texto final.',
  casual:
    'Reescreva o texto abaixo em tom casual e leve, mantendo o sentido e os fatos. Devolva somente o texto final.',
  straightforward:
    'Reescreva o texto abaixo em tom direto e objetivo, mantendo o sentido e os fatos. Devolva somente o texto final.',
  confident:
    'Reescreva o texto abaixo em tom confiante e seguro, mantendo o sentido e os fatos. Devolva somente o texto final.',
  friendly:
    'Reescreva o texto abaixo em tom amigável e acolhedor, mantendo o sentido e os fatos. Devolva somente o texto final.',
  make_friendly:
    'Reescreva o texto abaixo em tom amigável e acolhedor, mantendo o sentido e os fatos. Devolva somente o texto final.',
  make_formal:
    'Reescreva o texto abaixo em tom formal, mantendo o sentido e os fatos. Devolva somente o texto final.',
  expand:
    'Expanda o texto abaixo com mais detalhes úteis ao cliente, sem inventar fatos. Devolva somente o texto final.',
  shorten:
    'Encurte o texto abaixo, mantendo o sentido e os fatos essenciais. Devolva somente o texto final.',
  rephrase:
    'Reformule o texto abaixo com outras palavras, mantendo o sentido e os fatos. Devolva somente o texto final.',
  simplify:
    'Simplifique o texto abaixo para leitura fácil, mantendo o sentido e os fatos. Devolva somente o texto final.',
});

// Without an instruction every summary picked its own title and sections, and the model,
// which receives no sender names, filled in people that are not in the conversation.
// The title matches the one the Alex Copilot prompt already uses.
export const SUMMARY_INSTRUCTION = [
  'Resuma a conversa para a equipe interna, em português, em texto simples.',
  'Comece exatamente com a linha "Resumo para o atendente" e use, nesta ordem, os itens: Cliente, Pedido, O que já foi feito, Notas internas, Pendências. Omita um item sem informação.',
  'Use somente fatos e nomes que aparecem no texto das mensagens. Não invente nomes: quando o nome de alguém não aparece, escreva "o cliente" ou "o atendente".',
].join('\n');

const ERROR_MESSAGE_KEYS = Object.freeze({
  stale_conversation: 'ALEX_COPILOT.ERRORS.STALE',
  not_ready: 'ALEX_COPILOT.ERRORS.NOT_READY',
  unauthorized: 'ALEX_COPILOT.ERRORS.UNAUTHORIZED',
  route_unavailable: 'ALEX_COPILOT.ERRORS.ROUTE',
});

const clip = text =>
  text.length > MAX_TEXT_LENGTH ? text.slice(0, MAX_TEXT_LENGTH) : text;

const isAbort = error =>
  error?.name === CAPTAIN_ERROR_TYPES.ABORT_ERROR ||
  error?.name === CAPTAIN_ERROR_TYPES.CANCELED_ERROR ||
  error?.code === 'ERR_CANCELED';

/**
 * Builds the `rewrite` instruction for a native Copilot operation.
 * @param {string} operation - Native action key (improve, professional, ...).
 * @param {string} content - The attendant's current text.
 * @returns {string}
 */
export function buildRewriteInstruction(operation, content) {
  const instruction =
    REWRITE_INSTRUCTIONS[operation] || REWRITE_INSTRUCTIONS.improve;
  return clip(`${instruction}\n\nTexto do atendente:\n${content.trim()}`);
}

/**
 * Builds the `rewrite` instruction for a follow-up typed in the Copilot editor.
 * With a draft, the guidance refines it; without one, the guidance alone
 * (plus the conversation the server attaches) produces the reply.
 * @param {{draft?: string}} context - The follow-up context of the last result.
 * @param {string} message - The attendant's guidance.
 * @returns {string}
 */
export function buildFollowUpInstruction(context, message) {
  const draft = (context?.draft || '').trim();
  const parts = [`Orientação do atendente: ${message.trim()}`];
  if (draft) {
    parts.push(`Rascunho atual (revise conforme a orientação):\n${draft}`);
  } else {
    parts.push(
      'Não há rascunho ainda: escreva a resposta ao cliente a partir da orientação e do contexto da conversa. Devolva somente o texto final.'
    );
  }
  return clip(parts.join('\n\n'));
}

export function useAlexCopilot() {
  const { t } = useI18n();
  const accountId = useMapGetter('getCurrentAccountId');
  const getAccount = useMapGetter('accounts/getAccount');
  const currentChat = useMapGetter('getSelectedChat');

  const enabled = computed(() => {
    const account = getAccount.value?.(accountId.value);
    return account?.settings?.alex_captain_enabled === true;
  });

  const scope = () => ({
    accountId: Number(accountId.value),
    conversationId: Number(currentChat.value?.id),
    inboxId: Number(currentChat.value?.inbox_id),
  });

  const sameScope = (left, right) =>
    left.accountId === right.accountId &&
    left.conversationId === right.conversationId &&
    left.inboxId === right.inboxId;

  const isBoundSuccess = (data, target) =>
    data?.status === 'success' &&
    data.role === 'captain' &&
    data.version === ALEX_COPILOT_TRANSPORT_VERSION &&
    data.destination?.conversation?.displayId === target.conversationId &&
    data.destination?.inbox?.internalId === target.inboxId &&
    data.destination?.account?.internalId === target.accountId &&
    typeof data.result?.text === 'string' &&
    data.result.text.length > 0;

  const failure = (errorType, messageKey) => {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    if (messageKey) useAlert(t(messageKey));
    return { message: '', errorType };
  };

  const followUpContextFor = (task, text) => ({
    version: ALEX_COPILOT_FOLLOW_UP_VERSION,
    task,
    draft: text,
  });

  /**
   * Runs one transport task for the selected conversation.
   * @param {'suggest'|'summarize'|'rewrite'} task
   * @param {string} text - Instruction text (required for rewrite).
   * @param {AbortSignal} [signal]
   * @returns {Promise<{message: string, followUpContext?: Object, errorType?: string}>}
   */
  async function runTask(task, text, signal) {
    const target = scope();
    if (!target.accountId || !target.conversationId || !target.inboxId) {
      return failure(
        CAPTAIN_ERROR_TYPES.API_ERROR,
        'ALEX_COPILOT.ERRORS.GENERATE'
      );
    }
    // Stop (abort) also stops Alex on the server: provider call and remaining tool rounds.
    const clientRequestId = crypto.randomUUID();
    const cancelOnServer = () =>
      axios
        .post(
          `/api/v1/accounts/${target.accountId}/alex_captain/tasks/cancel`,
          {
            client_request_id: clientRequestId,
          }
        )
        .catch(() => {});
    signal?.addEventListener('abort', cancelOnServer, { once: true });
    let data;
    try {
      ({ data } = await axios.post(
        `/api/v1/accounts/${target.accountId}/alex_captain/tasks`,
        {
          task,
          text,
          conversation_display_id: target.conversationId,
          destination_conversation_display_id: target.conversationId,
          client_request_id: clientRequestId,
        },
        { signal, timeout: REQUEST_TIMEOUT_MS }
      ));
    } catch (error) {
      if (isAbort(error) || signal?.aborted) {
        return { message: '', errorType: CAPTAIN_ERROR_TYPES.ABORTED };
      }
      const status = error?.response?.status;
      const code = error?.response?.data?.error?.code;
      if (code === 'cancelled') {
        return { message: '', errorType: CAPTAIN_ERROR_TYPES.ABORTED };
      }
      return failure(
        status
          ? `${CAPTAIN_ERROR_TYPES.HTTP_PREFIX}${status}`
          : CAPTAIN_ERROR_TYPES.API_ERROR,
        ERROR_MESSAGE_KEYS[code] || 'ALEX_COPILOT.ERRORS.GENERATE'
      );
    }
    signal?.removeEventListener('abort', cancelOnServer);
    // The attendant moved on while the model was working: drop the result silently.
    if (!sameScope(scope(), target)) {
      return { message: '', errorType: CAPTAIN_ERROR_TYPES.ABORTED };
    }
    if (!isBoundSuccess(data, target)) {
      return failure(
        CAPTAIN_ERROR_TYPES.API_ERROR,
        ERROR_MESSAGE_KEYS[data?.error?.code] || 'ALEX_COPILOT.ERRORS.GENERATE'
      );
    }
    return {
      message: data.result.text,
      followUpContext: followUpContextFor(task, data.result.text),
    };
  }

  /**
   * Native Copilot entry point: maps the menu action to a transport task.
   * @param {string} type - Native action key.
   * @param {string} content - The attendant's current text (rewrite operations).
   * @param {{signal?: AbortSignal}} [options]
   */
  async function processEvent(type = 'improve', content = '', options = {}) {
    if (type === 'summarize') {
      return runTask('summarize', SUMMARY_INSTRUCTION, options.signal);
    }
    if (type === 'reply_suggestion') {
      return runTask('suggest', '', options.signal);
    }
    if (!(content || '').trim()) {
      return failure(
        CAPTAIN_ERROR_TYPES.API_ERROR,
        'ALEX_COPILOT.ERRORS.EMPTY_DRAFT'
      );
    }
    return runTask(
      'rewrite',
      buildRewriteInstruction(type, content),
      options.signal
    );
  }

  /**
   * Refines the last result with the guidance typed in the Copilot editor.
   * @param {{followUpContext: Object, message: string, signal?: AbortSignal}} options
   */
  async function followUp({ followUpContext, message, signal }) {
    if (
      followUpContext?.version !== ALEX_COPILOT_FOLLOW_UP_VERSION ||
      !(message || '').trim()
    ) {
      return {
        message: '',
        followUpContext,
        errorType: CAPTAIN_ERROR_TYPES.API_ERROR,
      };
    }
    const result = await runTask(
      'rewrite',
      buildFollowUpInstruction(followUpContext, message),
      signal
    );
    return result.message ? result : { ...result, followUpContext };
  }

  /**
   * Seeds the Copilot editor so the attendant can guide a draft without a
   * prior action ("Ask Copilot"). Keeping the current draft as the shown
   * content means accepting without a follow-up never erases their text.
   * @param {string} [draft] - The attendant's current text.
   */
  const guidedFollowUpContext = (draft = '') =>
    followUpContextFor('rewrite', (draft || '').trim());

  return {
    enabled,
    processEvent,
    followUp,
    guidedFollowUpContext,
  };
}
